import axios from 'axios'
import * as React from 'react'

import { TaskStatus } from '@/app/constants/taskStatus'
import { getActiveTasks } from '@/lib/api/config/importTask/active'
import { getImportTaskStatus } from '@/lib/api/config/importTask/status/getImportTaskStatus'
import { useTaskTrackerStore } from '@/modules/shared/stores/taskTrackerStore'

/**
 * The single polling loop for long-running tasks.
 *
 * Replaces a fan-out of one `/importTask/status` request per tracked task every
 * 500 ms (the code said "every 5 seconds"; the interval said 500). With a handful of
 * users on one company that alone was the heaviest traffic on the endpoint, and every
 * one of those requests pulled the task's whole graph. Now: one request for the whole
 * company, backing off while nothing changes, paused entirely while the tab is hidden.
 *
 * Fixing this before adding any rate limit is deliberate — a limit installed first
 * would have throttled our own polling before it ever throttled an attacker.
 */

/** Fast enough that a finished calculation feels immediate. */
const MIN_INTERVAL_MS = 1000
/** Only other people's work is running: we show it, but nobody here waits on it. */
const OTHERS_INTERVAL_MS = 3000
/**
 * Nothing running. Our own tasks wake the loop on registration, so this only delays
 * noticing a colleague's task — and the server's ScopeKey check covers that race.
 */
const MAX_INTERVAL_MS = 15000
/** Unchanged polls before we start backing off. */
const IDLE_POLLS_BEFORE_BACKOFF = 3

export const useTaskSync = (companyId: string | null | undefined) => {
  const syncFromServer = useTaskTrackerStore((s) => s.syncFromServer)
  const settle = useTaskTrackerStore((s) => s.settle)

  // Read tasks imperatively: this loop must not restart on every progress tick.
  const tasksRef = React.useRef(useTaskTrackerStore.getState().tasks)
  React.useEffect(
    () => useTaskTrackerStore.subscribe((s) => { tasksRef.current = s.tasks }),
    [],
  )

  React.useEffect(() => {
    if (!companyId) return undefined

    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null
    let idlePolls = 0
    // True while a poll request is outstanding — see onVisibilityChange.
    let inFlight = false
    // A task was registered while a poll was outstanding: poll again as soon as it lands.
    let pollAgain = false
    // True while the server reports at least one running task — see schedule().
    let anyTaskRunning = false
    let lastSignature = ''
    // Tasks already sent for a one-off status lookup, so a slow lookup is not
    // re-issued by the next poll.
    const resolving = new Set<string>()

    const ownTasksFor = (company: string) =>
      Object.values(tasksRef.current).filter(
        (t) =>
          t.companyId === company &&
          t.status !== TaskStatus.Completed &&
          t.status !== TaskStatus.Error,
      )

    /**
     * A task that vanished from the active list has finished — but the list cannot
     * say whether it succeeded or failed, and the completion handlers need that
     * distinction (and the error message). One lookup per task, never in a loop.
     */
    const resolveVanished = async (id: string) => {
      if (resolving.has(id)) return
      resolving.add(id)
      try {
        const response = await getImportTaskStatus({ importTaskId: id })
        const payload: any = response?.data ?? {}
        const status = payload.status ?? payload.Status
        if (!cancelled && status != null) {
          settle(
            id,
            status,
            payload.message ?? payload.Message,
            payload.logs ?? payload.Logs,
          )
        }
      } catch (error) {
        // ONLY 404 means the task is really gone. Settling on any failure turned a
        // single 502 or dropped connection into "your calculation failed": the user
        // got an error toast and the completion side effects (file download, print
        // batch, co-driver dialog) never ran, even though the work had succeeded.
        // Anything else: leave it alone, the next poll will ask again.
        if (!cancelled && (error as any)?.response?.status === 404) {
          settle(id, TaskStatus.Error)
        }
      } finally {
        resolving.delete(id)
      }
    }

    const poll = async () => {
      if (cancelled) return
      inFlight = true
      pollAgain = false
      const knownAtStart = new Set(Object.keys(useTaskTrackerStore.getState().tasks))

      // Nothing to watch and nobody else is running anything we know of — the next
      // poll still has to happen (a colleague may start something), just slowly.
      try {
        const active = await getActiveTasks(companyId)
        if (cancelled) return

        syncFromServer(companyId, active)

        const activeIds = new Set(active.map((t) => t.id))
        ownTasksFor(companyId).forEach((task) => {
          if (activeIds.has(task.id)) return
          // The server creates the task before handing us its id, so only a poll
          // started before registration can miss a running task. A fixed 5 s grace
          // here held every quick printout back by up to 5 s.
          if (!knownAtStart.has(task.id)) return
          void resolveVanished(task.id)
        })

        anyTaskRunning = active.length > 0

        const signature = active
          .map((t) => `${t.id}:${t.status}:${t.progressCurrent ?? ''}`)
          .sort()
          .join('|')
        idlePolls = signature === lastSignature ? idlePolls + 1 : 0
        lastSignature = signature
      } catch (error) {
        // Bez uprawnień do zadań firmy kolejne odpytania też dostaną 403.
        if (axios.isAxiosError(error) && error.response?.status === 403) {
          cancelled = true
          inFlight = false
          return
        }
        // A failed poll is not worth surfacing: the next one will either work or
        // the user will notice through the request that actually matters.
        idlePolls += 1
      }

      inFlight = false
      if (pollAgain && !cancelled && document.visibilityState !== 'hidden') {
        void poll()
        return
      }
      schedule()
    }

    // A freshly registered task must not wait out a backed-off 5 s delay.
    const wake = () => {
      if (cancelled || document.visibilityState === 'hidden') return
      idlePolls = 0
      if (inFlight) {
        pollAgain = true
        return
      }
      if (timer) clearTimeout(timer)
      timer = null
      void poll()
    }

    const schedule = () => {
      // Hidden tab: stop the chain entirely. Restarted by onVisibilityChange.
      if (cancelled || document.visibilityState === 'hidden') return

      // Nie zwalniamy, dopoki leci nasze zadanie. Backoff patrzyl tylko na to, czy
      // odpowiedz sie zmienila - a dlugie zadanie, ktore nie raportuje postepu (np.
      // wydruk calej firmy), wyglada wtedy jak cisza i polling schodzil do 5 s
      // dokladnie wtedy, gdy uzytkownik najbardziej czeka na koniec. Na cudze
      // zadania nikt tu nie czeka, wiec wystarczy rzadszy odczyt.
      const ownRunning = !!companyId && ownTasksFor(companyId).some((t) => t.mine)
      const delay = ownRunning
        ? MIN_INTERVAL_MS
        : anyTaskRunning
          ? OTHERS_INTERVAL_MS
          : idlePolls >= IDLE_POLLS_BEFORE_BACKOFF
            ? MAX_INTERVAL_MS
            : MIN_INTERVAL_MS
      timer = setTimeout(poll, delay)
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        if (timer) clearTimeout(timer)
        timer = null
        return
      }
      // Back in view: poll straight away rather than after the pending delay, and
      // reset the backoff so a returning user sees fresh state immediately.
      //
      // `inFlight` is what makes this safe. Hiding the tab mid-request nulled `timer`
      // while a poll was still running; that poll then called schedule() and set a
      // fresh timer, so showing the tab again saw `!timer`, started a SECOND chain,
      // and every hide/show during a request added another one. Polling multiplied
      // without bound and never actually paused.
      if (!timer && !inFlight) {
        idlePolls = 0
        void poll()
      }
    }

    const unsubscribe = useTaskTrackerStore.subscribe((state, previous) => {
      const registered = Object.keys(state.tasks).some(
        (id) =>
          !previous.tasks[id] &&
          state.tasks[id].mine &&
          state.tasks[id].companyId === companyId,
      )
      if (registered) wake()
    })

    void poll()
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      cancelled = true
      unsubscribe()
      if (timer) clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [companyId, syncFromServer, settle])
}
